import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Grid,
  MenuItem,
  IconButton,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import { showSuccess, showError } from '../utils/notifications';
import { ConfirmDialog, TableSkeleton, ActionButton, DataTable, FilterBar } from '../components';

const MotionBox = motion(Box);

function Equipment() {
  const [equipment, setEquipment] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, equipmentId: null });
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [operationalStatusFilter, setOperationalStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [criticalityFilter, setCriticalityFilter] = useState('all');
  const [formData, setFormData] = useState({
    equipment_id: undefined,
    equipment_name: '',
    equipment_code: '',
    model_id: '',
    equipment_type: '',
    equipment_subtype: '',
    manufacturer: '',
    manufacturer_model: '',
    serial_number: '',
    installation_date: '',
    commissioned_date: '',
    warranty_expiry: '',
    maintenance_interval_days: '',
    operational_status: 1,
    criticality_level: 3,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [equipmentRes, modelsRes] = await Promise.all([
        API.equipment.list(),
        API.models.list(),
      ]);
      setEquipment(equipmentRes?.data || []);
      setModels(modelsRes?.data || []);
    } catch (err) {
      showError('Failed to load equipment');
      console.error('Equipment fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingEquipment(item);
      setFormData({
        equipment_id: item.equipment_id,
        equipment_name: item.equipment_name || '',
        equipment_code: item.equipment_code || '',
        model_id: item.model_id || '',
        equipment_type: item.equipment_type || '',
        equipment_subtype: item.equipment_subtype || '',
        manufacturer: item.manufacturer || '',
        manufacturer_model: item.manufacturer_model || '',
        serial_number: item.serial_number || '',
        installation_date: item.installation_date ? item.installation_date.split('T')[0] : '',
        commissioned_date: item.commissioned_date ? item.commissioned_date.split('T')[0] : '',
        warranty_expiry: item.warranty_expiry ? item.warranty_expiry.split('T')[0] : '',
        maintenance_interval_days: item.maintenance_interval_days || '',
        operational_status: item.operational_status !== undefined ? item.operational_status : 1,
        criticality_level: item.criticality_level || 3,
      });
    } else {
      setEditingEquipment(null);
      setFormData({
        equipment_id: undefined,
        equipment_name: '',
        equipment_code: '',
        model_id: '',
        equipment_type: '',
        equipment_subtype: '',
        manufacturer: '',
        manufacturer_model: '',
        serial_number: '',
        installation_date: '',
        commissioned_date: '',
        warranty_expiry: '',
        maintenance_interval_days: '',
        operational_status: 1,
        criticality_level: 3,
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingEquipment(null);
    setFormData({
      equipment_id: undefined,
      equipment_name: '',
      equipment_code: '',
      model_id: '',
      equipment_type: '',
      equipment_subtype: '',
      manufacturer: '',
      manufacturer_model: '',
      serial_number: '',
      installation_date: '',
      commissioned_date: '',
      warranty_expiry: '',
      maintenance_interval_days: '',
      operational_status: 1,
      criticality_level: 3,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingEquipment) {
        await API.equipment.update(editingEquipment.equipment_id, formData);
        showSuccess('Equipment updated successfully!');
      } else {
        const { equipment_id, ...dataToCreate } = formData;
        await API.equipment.create(dataToCreate);
        showSuccess('Equipment created successfully!');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (editingEquipment ? 'Failed to update equipment' : 'Failed to create equipment');
      showError(errorMessage);
      console.error('Equipment submit error:', err);
    }
  };

  const handleDeleteClick = (equipmentId) => {
    setConfirmDialog({ open: true, equipmentId });
  };

  const handleDelete = async () => {
    try {
      await API.equipment.delete(confirmDialog.equipmentId);
      showSuccess('Equipment deleted successfully!');
      fetchData();
    } catch (err) {
      showError('Failed to delete equipment');
      console.error('Equipment delete error:', err);
    }
  };

  const handleStatusToggle = async (equipmentId, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await API.equipment.update(equipmentId, { operational_status: newStatus });
      showSuccess(`Equipment ${newStatus === 1 ? 'activated' : 'deactivated'} successfully!`);
      fetchData();
    } catch (err) {
      showError('Failed to update equipment status');
      console.error('Equipment status update error:', err);
    }
  };

  const getModelName = (modelId) => {
    const model = models.find(m => m.model_id === modelId);
    return model ? model.model_name : 'Unknown';
  };

  const getOperationalStatusLabel = (status) => {
    switch (status) {
      case 1: return 'Operational';
      case 2: return 'Maintenance';
      case 3: return 'Standby';
      case 0: return 'Offline';
      default: return 'Unknown';
    }
  };

  const getOperationalStatusColor = (status) => {
    switch (status) {
      case 1: return { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' }; // Operational - Green
      case 2: return { bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' }; // Maintenance - Yellow
      case 3: return { bg: '#DBEAFE', color: '#1E40AF', border: '#93C5FD' }; // Standby - Blue
      case 0: return { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' }; // Offline - Red
      default: return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' };
    }
  };

  const getCriticalityLabel = (level) => {
    switch (level) {
      case 1: return 'Critical';
      case 2: return 'High';
      case 3: return 'Medium';
      case 4: return 'Low';
      default: return 'Unknown';
    }
  };

  const filteredEquipment = equipment.filter(item => {
    const matchesSearch = item.equipment_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.equipment_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.equipment_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesOperationalStatus = operationalStatusFilter === 'all' || item.operational_status === parseInt(operationalStatusFilter);
    const matchesType = typeFilter === 'all' || item.equipment_type === typeFilter;
    const matchesCriticality = criticalityFilter === 'all' || item.criticality_level === parseInt(criticalityFilter);
    
    return matchesSearch && matchesOperationalStatus && matchesType && matchesCriticality;
  });

  const uniqueTypes = [...new Set(equipment.map(e => e.equipment_type).filter(Boolean))];

  const columns = [
    {
      field: 'equipment_name',
      headerName: 'Equipment Name',
      width: '18%',
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.equipment_name}
        </Typography>
      ),
    },
    {
      field: 'equipment_code',
      headerName: 'Code',
      width: '10%',
      render: (row) => (
        <Chip 
          label={row.equipment_code} 
          size="small" 
          sx={{ 
            fontWeight: 600,
            fontSize: '0.7rem',
            backgroundColor: '#EEF2FF',
            color: '#4F46E5',
            border: '1px solid #C7D2FE',
          }}
        />
      ),
    },
    {
      field: 'equipment_type',
      headerName: 'Type',
      width: '12%',
    },
    {
      field: 'equipment_subtype',
      headerName: 'Subtype',
      width: '10%',
      render: (row) => row.equipment_subtype || '-',
    },
    {
      field: 'manufacturer',
      headerName: 'Manufacturer',
      width: '12%',
      render: (row) => row.manufacturer || '-',
    },
    {
      field: 'manufacturer_model',
      headerName: 'Model',
      width: '10%',
      render: (row) => row.manufacturer_model || '-',
    },
    {
      field: 'serial_number',
      headerName: 'Serial #',
      width: '10%',
      render: (row) => row.serial_number || '-',
    },
    {
      field: 'installation_date',
      headerName: 'Installed',
      width: '10%',
      render: (row) => row.installation_date ? new Date(row.installation_date).toLocaleDateString() : '-',
    },
    {
      field: 'operational_status',
      headerName: 'Status',
      width: '12%',
      sortable: false,
      render: (row) => {
        const colors = getOperationalStatusColor(row.operational_status);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={row.operational_status === 1}
              onChange={() => handleStatusToggle(row.equipment_id, row.operational_status)}
              size="small"
              color="primary"
            />
            <Chip
              label={getOperationalStatusLabel(row.operational_status)}
              size="small"
              sx={{ 
                fontWeight: 600,
                fontSize: '0.7rem',
                backgroundColor: colors.bg,
                color: colors.color,
                border: `1px solid ${colors.border}`,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: '10%',
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-',
    },
  ];

  const filterFields = [
    {
      label: 'Operational Status',
      value: operationalStatusFilter,
      onChange: setOperationalStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: '1', label: 'Operational' },
        { value: '2', label: 'Maintenance' },
        { value: '3', label: 'Standby' },
        { value: '0', label: 'Offline' },
      ],
    },
    {
      label: 'Equipment Type',
      value: typeFilter,
      onChange: setTypeFilter,
      options: [
        { value: 'all', label: 'All Types' },
        ...uniqueTypes.map(type => ({ value: type, label: type })),
      ],
    },
    {
      label: 'Criticality Level',
      value: criticalityFilter,
      onChange: setCriticalityFilter,
      options: [
        { value: 'all', label: 'All Levels' },
        { value: '1', label: 'Critical' },
        { value: '2', label: 'High' },
        { value: '3', label: 'Medium' },
        { value: '4', label: 'Low' },
      ],
    },
  ];

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            Equipment
          </Typography>
        </Box>
        <TableSkeleton columns={7} rows={6} />
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Equipment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage equipment inventory and assignments
          </Typography>
        </Box>
        <ActionButton
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Equipment
        </ActionButton>
      </Box>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search equipment..."
        filterFields={filterFields}
      />

      <DataTable
        columns={columns}
        data={filteredEquipment}
        onEdit={handleOpenDialog}
        onDelete={(row) => handleDeleteClick(row.equipment_id)}
        getRowId={(row) => row.equipment_id}
        emptyMessage="No equipment found"
      />

      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
        }}>
          <Typography variant="h6" fontWeight={700}>
            {editingEquipment ? 'Edit Equipment' : 'Add New Equipment'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Equipment Name"
                value={formData.equipment_name}
                onChange={(e) => setFormData({ ...formData, equipment_name: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Equipment Code"
                value={formData.equipment_code}
                onChange={(e) => setFormData({ ...formData, equipment_code: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Model"
                value={formData.model_id}
                onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {models.map((model) => (
                  <MenuItem key={model.model_id} value={model.model_id}>
                    {model.model_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Equipment Type"
                value={formData.equipment_type}
                onChange={(e) => setFormData({ ...formData, equipment_type: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Equipment Subtype"
                value={formData.equipment_subtype}
                onChange={(e) => setFormData({ ...formData, equipment_subtype: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manufacturer Model"
                value={formData.manufacturer_model}
                onChange={(e) => setFormData({ ...formData, manufacturer_model: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Serial Number"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Installation Date"
                type="date"
                value={formData.installation_date}
                onChange={(e) => setFormData({ ...formData, installation_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Commissioned Date"
                type="date"
                value={formData.commissioned_date}
                onChange={(e) => setFormData({ ...formData, commissioned_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Warranty Expiry"
                type="date"
                value={formData.warranty_expiry}
                onChange={(e) => setFormData({ ...formData, warranty_expiry: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maintenance Interval (Days)"
                type="number"
                value={formData.maintenance_interval_days}
                onChange={(e) => setFormData({ ...formData, maintenance_interval_days: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Operational Status"
                value={formData.operational_status}
                onChange={(e) => setFormData({ ...formData, operational_status: parseInt(e.target.value) })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value={1}>Operational</MenuItem>
                <MenuItem value={2}>Maintenance</MenuItem>
                <MenuItem value={3}>Standby</MenuItem>
                <MenuItem value={0}>Offline</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Criticality Level"
                value={formData.criticality_level}
                onChange={(e) => setFormData({ ...formData, criticality_level: parseInt(e.target.value) })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value={1}>Critical</MenuItem>
                <MenuItem value={2}>High</MenuItem>
                <MenuItem value={3}>Medium</MenuItem>
                <MenuItem value={4}>Low</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <ActionButton variant="outlined" onClick={handleCloseDialog}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" onClick={handleSubmit}>
            {editingEquipment ? 'Update Equipment' : 'Create Equipment'}
          </ActionButton>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, equipmentId: null })}
        onConfirm={handleDelete}
        title="Delete Equipment"
        message="Are you sure you want to delete this equipment? This action cannot be undone."
        confirmText="Delete"
        type="danger"
      />
    </MotionBox>
  );
}

export default Equipment;